import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Info,
  MoreHorizontal,
  Pause,
  Play,
  Power,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  Shield,
  Snowflake,
  Zap,
} from 'lucide-react'
import { adminApi } from '@/services/adminApi'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type HealthRow = Awaited<ReturnType<typeof adminApi.getRoutingHealth>>['rows'][number]
type StickySummary = Awaited<ReturnType<typeof adminApi.getStickyPinSummary>>
type RoutingChain = Awaited<ReturnType<typeof adminApi.getRoutingChains>>['chains'][number]
type ChainCandidate = RoutingChain['candidates'][number]
type Thresholds = HealthRow['thresholds']
type ProbeReport = Awaited<ReturnType<typeof adminApi.runRoutingProbe>>
type RecoveryStatus = Awaited<ReturnType<typeof adminApi.getRoutingRecoveryStatus>>
type RecoveryEvent = RecoveryStatus['recentEvents'][number]

interface ThresholdEditTarget {
  llmModelId: string
  provider: string
  modelName: string
  thresholds: Thresholds
}

type Tab = 'overview' | 'candidates' | 'activity'
type CandidateFilter = 'all' | 'degraded' | 'cooled' | 'retired' | 'healthy'

const REFRESH_INTERVAL_MS = 15_000

const DEFAULT_THRESHOLDS: Thresholds = {
  errorRateThresholdPct: 50,
  ttftDegradedThresholdMs: 10_000,
  ttftPerTokenMs: 0,
  tpsDegradedThresholdMin: 10,
  minSamplesForTrigger: 10,
  latencyCooldownMs: 60_000,
  recoverySkipAfterFailures: 5,
  autoRetireAfterFailures: 8,
}

const THRESHOLD_PRESETS: Record<'strict' | 'default' | 'lenient', { label: string; description: string; values: Thresholds }> = {
  strict: {
    label: 'Strict',
    description: 'Fail fast: small samples, low tolerance for slow/erroring models.',
    values: {
      errorRateThresholdPct: 25,
      ttftDegradedThresholdMs: 6_000,
      ttftPerTokenMs: 0,
      tpsDegradedThresholdMin: 15,
      minSamplesForTrigger: 5,
      latencyCooldownMs: 120_000,
      recoverySkipAfterFailures: 3,
      autoRetireAfterFailures: 5,
    },
  },
  default: {
    label: 'Default',
    description: 'Balanced. The platform default.',
    values: DEFAULT_THRESHOLDS,
  },
  lenient: {
    label: 'Lenient',
    description: 'Tolerate noisy models: more samples needed, higher latency budget.',
    values: {
      errorRateThresholdPct: 70,
      ttftDegradedThresholdMs: 20_000,
      ttftPerTokenMs: 0,
      tpsDegradedThresholdMin: 5,
      minSamplesForTrigger: 20,
      latencyCooldownMs: 30_000,
      recoverySkipAfterFailures: 8,
      autoRetireAfterFailures: 12,
    },
  },
}

export default function RoutingHealthPage() {
  const [rows, setRows] = useState<HealthRow[]>([])
  const [catalogSize, setCatalogSize] = useState(0)
  const [stickySummary, setStickySummary] = useState<StickySummary | null>(null)
  const [chains, setChains] = useState<RoutingChain[]>([])
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null)

  const [tab, setTab] = useState<Tab>('overview')
  const [chainQuery, setChainQuery] = useState('')
  const [candidateQuery, setCandidateQuery] = useState('')
  const [candidateFilter, setCandidateFilter] = useState<CandidateFilter>('all')
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null)
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)

  const [editTarget, setEditTarget] = useState<ThresholdEditTarget | null>(null)
  const [probeReport, setProbeReport] = useState<ProbeReport | null>(null)
  const [probingProductModelId, setProbingProductModelId] = useState<string | null>(null)
  const [runningRecovery, setRunningRecovery] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [routingFeatureEnabled, setRoutingFeatureEnabled] = useState<boolean | null>(null)
  const [togglingFeature, setTogglingFeature] = useState(false)

  const load = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true)
      const [overview, sticky, chainsResult, recovery, featureFlag] = await Promise.all([
        adminApi.getRoutingHealth(),
        adminApi.getStickyPinSummary(),
        adminApi.getRoutingChains(),
        adminApi.getRoutingRecoveryStatus(),
        adminApi.getRoutingFeatureEnabled(),
      ])
      setRows(overview.rows)
      setCatalogSize(overview.catalogSize)
      setStickySummary(sticky)
      setChains(chainsResult.chains)
      setRecoveryStatus(recovery)
      setRoutingFeatureEnabled(featureFlag.enabled)
      setLastRefreshedAt(new Date())
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load routing health')
    } finally {
      if (!silent) setRefreshing(false)
      setLoading(false)
    }
  }

  const onToggleRoutingFeature = async () => {
    if (routingFeatureEnabled === null || togglingFeature) return
    const next = !routingFeatureEnabled
    if (!next && !window.confirm(
      'Disable routing globally? Every request will go straight to the resolved primary candidate — no failover, no fallback race, no cooldown skipping. Re-enable any time.',
    )) return
    setTogglingFeature(true)
    try {
      await adminApi.setRoutingFeatureEnabled(next)
      setRoutingFeatureEnabled(next)
      toast.success(next ? 'Routing enabled' : 'Routing disabled — using primary directly')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to toggle routing')
    } finally {
      setTogglingFeature(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(() => load(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [autoRefresh])

  const onClearCooldown = async (row: HealthRow) => {
    try {
      await adminApi.clearRoutingCooldown(row.provider, row.modelName)
      toast.success(`Cooldown cleared for ${row.provider}/${row.modelName}`)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to clear cooldown')
    }
  }

  const onSaveThresholds = async (
    target: ThresholdEditTarget,
    next: Thresholds,
  ): Promise<void> => {
    try {
      await adminApi.updateLLMModelThresholds(target.llmModelId, next)
      toast.success(`Thresholds updated for ${target.provider}/${target.modelName}`)
      setEditTarget(null)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update thresholds')
    }
  }

  const onRunRecoveryNow = async () => {
    try {
      setRunningRecovery(true)
      const result = await adminApi.runRoutingRecoveryNow()
      toast.success(
        `Recovery scan complete · probed ${result.candidatesProbed}, recovered ${result.recovered}, extended ${result.extended}, retired ${result.autoRetired}`,
      )
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Recovery scan failed')
    } finally {
      setRunningRecovery(false)
    }
  }

  const onRunProbe = async (productModelId: string) => {
    try {
      setProbingProductModelId(productModelId)
      const report = await adminApi.runRoutingProbe(productModelId)
      setProbeReport(report)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Probe failed')
    } finally {
      setProbingProductModelId(null)
    }
  }

  const onUnretireKey = async (row: HealthRow) => {
    try {
      await adminApi.unretireRoutingKey(row.provider, row.modelName)
      toast.success(`Key unretired for ${row.provider}/${row.modelName}`)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to unretire key')
    }
  }

  const onForceCooldown = async (row: HealthRow) => {
    const minutesInput = window.prompt(
      `Cooldown ${row.provider}/${row.modelName} for how many minutes?`,
      '5',
    )
    if (!minutesInput) return
    const mins = parseInt(minutesInput, 10)
    if (!Number.isFinite(mins) || mins <= 0) {
      toast.error('Enter a positive number of minutes')
      return
    }
    try {
      await adminApi.forceRoutingCooldown(row.provider, row.modelName, mins * 60_000)
      toast.success(`Cooldown applied for ${mins} min`)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to apply cooldown')
    }
  }

  const onForceRetire = async (row: HealthRow) => {
    if (
      !window.confirm(
        `Retire ${row.provider}/${row.modelName} permanently? Routing will skip this candidate until you unretire it manually.`,
      )
    )
      return
    try {
      await adminApi.forceRetireRoutingKey(row.provider, row.modelName)
      toast.success(`Key retired for ${row.provider}/${row.modelName}`)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to retire key')
    }
  }

  const onToggleEnabled = async (row: HealthRow) => {
    const turningOff = !row.manuallyDisabled
    if (turningOff && !window.confirm(
      `Disable ${row.provider}/${row.modelName}? Routing will skip this candidate until you re-enable it.`,
    )) return
    try {
      await adminApi.setCandidateEnabled(row.provider, row.modelName, !turningOff)
      toast.success(turningOff
        ? `Disabled ${row.provider}/${row.modelName}`
        : `Enabled ${row.provider}/${row.modelName}`)
      await load(true)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to toggle candidate')
    }
  }

  const degradedRows = useMemo(() => rows.filter(r => r.isDegraded), [rows])
  const degradedChains = useMemo(() => chains.filter(c => c.candidates.some(x => x.isDegraded)), [chains])
  const cooledCount = rows.filter(r => r.cooldownActive).length
  const retiredCount = rows.filter(r => r.keyRetired).length

  const filteredChains = useMemo(() => {
    const q = chainQuery.trim().toLowerCase()
    const list = !q
      ? chains
      : chains.filter(c =>
          c.displayName.toLowerCase().includes(q) ||
          c.candidates.some(x => `${x.provider}/${x.modelName}`.toLowerCase().includes(q)),
        )
    return [...list].sort((a, b) => {
      const aDeg = a.candidates.some(x => x.isDegraded) ? 1 : 0
      const bDeg = b.candidates.some(x => x.isDegraded) ? 1 : 0
      return bDeg - aDeg
    })
  }, [chains, chainQuery])

  const filteredRows = useMemo(() => {
    const q = candidateQuery.trim().toLowerCase()
    return rows
      .filter(r => {
        if (candidateFilter === 'degraded' && !r.isDegraded) return false
        if (candidateFilter === 'cooled' && !r.cooldownActive) return false
        if (candidateFilter === 'retired' && !r.keyRetired) return false
        if (candidateFilter === 'healthy' && r.isDegraded) return false
        if (!q) return true
        return (
          r.provider.toLowerCase().includes(q) ||
          r.modelName.toLowerCase().includes(q) ||
          r.referencedBy.some(x => x.toLowerCase().includes(q))
        )
      })
      .sort((a, b) => {
        if (a.isDegraded !== b.isDegraded) return a.isDegraded ? -1 : 1
        return a.provider.localeCompare(b.provider)
      })
  }, [rows, candidateQuery, candidateFilter])

  const overallTone: 'ok' | 'warn' | 'crit' =
    retiredCount > 0 ? 'crit' : degradedRows.length > 0 ? 'warn' : 'ok'

  return (
    <div className="space-y-5" onClick={() => setOpenMenuKey(null)}>
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Routing Health</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Every (provider, model) the hardmode routing layer can pick from, with live health from a rolling 1-hour window. Use this page to see if routing is working, fix degraded candidates, and tune when routing should bypass them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHelpOpen(v => !v)}
            title="How to read and test this page"
          >
            <HelpCircle className="mr-1.5 h-3.5 w-3.5" />
            {helpOpen ? 'Hide help' : 'How to use'}
          </Button>
          <button
            type="button"
            onClick={() => setAutoRefresh(v => !v)}
            className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh (15s)'}
          >
            {autoRefresh ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {autoRefresh ? 'Auto' : 'Paused'}
          </button>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={refreshing}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {helpOpen && <HelpCard onClose={() => setHelpOpen(false)} />}

      <RoutingFeatureToggle
        enabled={routingFeatureEnabled}
        toggling={togglingFeature}
        onToggle={onToggleRoutingFeature}
      />

      <HealthBanner
        tone={overallTone}
        degradedCount={degradedRows.length}
        retiredCount={retiredCount}
        cooledCount={cooledCount}
        catalogSize={catalogSize}
        candidateCount={rows.length}
        stickyPins={stickySummary?.totalPinned ?? null}
        lastRefreshedAt={lastRefreshedAt}
        onJumpToDegraded={() => {
          setTab('candidates')
          setCandidateFilter('degraded')
        }}
      />

      <nav className="flex items-center gap-1 border-b border-gray-200">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
          Chains
        </TabButton>
        <TabButton active={tab === 'candidates'} onClick={() => setTab('candidates')}>
          Candidates
          <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
            {rows.length}
          </span>
        </TabButton>
        <TabButton active={tab === 'activity'} onClick={() => setTab('activity')}>
          Activity
          {recoveryStatus && recoveryStatus.extendedCooldowns.length > 0 && (
            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              {recoveryStatus.extendedCooldowns.length}
            </span>
          )}
        </TabButton>
      </nav>

      <TabSubheader tab={tab} />

      {tab === 'overview' && (
        <OverviewPanel
          loading={loading}
          chains={filteredChains}
          totalChains={chains.length}
          chainQuery={chainQuery}
          onChainQuery={setChainQuery}
          degradedChainsCount={degradedChains.length}
          isProbing={productModelId => probingProductModelId === productModelId}
          onRunProbe={onRunProbe}
          onEditCandidate={candidate => {
            if (!candidate.llmModelId) return
            setEditTarget({
              llmModelId: candidate.llmModelId,
              provider: candidate.provider,
              modelName: candidate.modelName,
              thresholds: candidate.thresholds,
            })
          }}
        />
      )}

      {tab === 'candidates' && (
        <CandidatesPanel
          loading={loading}
          rows={filteredRows}
          totalCount={rows.length}
          query={candidateQuery}
          onQuery={setCandidateQuery}
          filter={candidateFilter}
          onFilter={setCandidateFilter}
          counts={{
            degraded: degradedRows.length,
            cooled: cooledCount,
            retired: retiredCount,
            healthy: rows.length - degradedRows.length,
          }}
          expandedRowKey={expandedRowKey}
          onToggleExpand={setExpandedRowKey}
          openMenuKey={openMenuKey}
          onOpenMenu={setOpenMenuKey}
          onEditThresholds={row => {
            if (!row.llmModelId) return
            setEditTarget({
              llmModelId: row.llmModelId,
              provider: row.provider,
              modelName: row.modelName,
              thresholds: row.thresholds,
            })
          }}
          onClearCooldown={onClearCooldown}
          onForceCooldown={onForceCooldown}
          onUnretireKey={onUnretireKey}
          onForceRetire={onForceRetire}
          onToggleEnabled={onToggleEnabled}
        />
      )}

      {tab === 'activity' && (
        <ActivityPanel
          recoveryStatus={recoveryStatus}
          stickySummary={stickySummary}
          onRunRecoveryNow={onRunRecoveryNow}
          isRecoveryRunning={runningRecovery}
        />
      )}

      {editTarget && (
        <ThresholdEditorModal
          target={editTarget}
          onCancel={() => setEditTarget(null)}
          onSave={onSaveThresholds}
        />
      )}

      {probeReport && (
        <ProbeResultsModal report={probeReport} onClose={() => setProbeReport(null)} />
      )}
    </div>
  )
}

function RoutingFeatureToggle({
  enabled,
  toggling,
  onToggle,
}: {
  enabled: boolean | null
  toggling: boolean
  onToggle: () => void
}) {
  if (enabled === null) return null
  const tone = enabled
    ? { wrap: 'border-emerald-200 bg-emerald-50', dot: 'bg-emerald-500', label: 'text-emerald-900' }
    : { wrap: 'border-amber-300 bg-amber-50', dot: 'bg-amber-500', label: 'text-amber-900' }
  return (
    <div className={`flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center md:justify-between ${tone.wrap}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 inline-block h-2 w-2 rounded-full ${tone.dot}`} />
        <div>
          <div className={`text-sm font-semibold ${tone.label}`}>
            {enabled ? 'Routing feature is ON' : 'Routing feature is OFF'}
          </div>
          <div className="mt-0.5 text-xs text-gray-700">
            {enabled
              ? 'Health-based candidate selection, failover, parallel fallback race, cooldowns, and recovery probes are all active.'
              : 'All requests bypass routing and call the resolved primary directly. No failover, no fallback race, no cooldown skipping. Per-model thresholds are ignored.'}
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        disabled={toggling}
        className={enabled ? 'border-amber-300 text-amber-900 hover:bg-amber-100' : 'border-emerald-300 text-emerald-900 hover:bg-emerald-100'}
      >
        <Power className="mr-1.5 h-3.5 w-3.5" />
        {toggling ? 'Saving…' : enabled ? 'Turn OFF routing' : 'Turn ON routing'}
      </Button>
    </div>
  )
}

function HealthBanner({
  tone,
  degradedCount,
  retiredCount,
  cooledCount,
  catalogSize,
  candidateCount,
  stickyPins,
  lastRefreshedAt,
  onJumpToDegraded,
}: {
  tone: 'ok' | 'warn' | 'crit'
  degradedCount: number
  retiredCount: number
  cooledCount: number
  catalogSize: number
  candidateCount: number
  stickyPins: number | null
  lastRefreshedAt: Date | null
  onJumpToDegraded: () => void
}) {
  const palette =
    tone === 'crit'
      ? { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', icon: 'text-red-600' }
      : tone === 'warn'
        ? { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: 'text-amber-600' }
        : { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', icon: 'text-emerald-600' }

  const headline =
    tone === 'crit'
      ? `${retiredCount} key${retiredCount === 1 ? '' : 's'} retired, ${degradedCount} candidate${degradedCount === 1 ? '' : 's'} degraded`
      : tone === 'warn'
        ? `${degradedCount} candidate${degradedCount === 1 ? '' : 's'} degraded`
        : 'All candidates healthy'

  const subline =
    tone === 'ok'
      ? `${candidateCount} tracked candidate${candidateCount === 1 ? '' : 's'} across ${catalogSize} ProductModel${catalogSize === 1 ? '' : 's'}`
      : 'Some candidates are bypassed by routing. Investigate below.'

  return (
    <div className={`flex flex-col gap-3 rounded-lg border ${palette.border} ${palette.bg} px-4 py-3 md:flex-row md:items-center md:justify-between`}>
      <div className="flex items-start gap-3">
        {tone === 'ok' ? (
          <CheckCircle2 className={`mt-0.5 h-5 w-5 ${palette.icon}`} />
        ) : (
          <AlertTriangle className={`mt-0.5 h-5 w-5 ${palette.icon}`} />
        )}
        <div>
          <div className={`text-sm font-semibold ${palette.text}`}>{headline}</div>
          <div className={`text-xs ${palette.text} opacity-80`}>{subline}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-700">
        <MiniStat label="Catalog" value={catalogSize} />
        <MiniStat label="Tracked" value={candidateCount} />
        <MiniStat label="Cooled" value={cooledCount} tone={cooledCount > 0 ? 'warn' : undefined} />
        <MiniStat label="Retired" value={retiredCount} tone={retiredCount > 0 ? 'crit' : undefined} />
        <MiniStat label="Sticky pins" value={stickyPins ?? '—'} />
        {lastRefreshedAt && (
          <span className="text-gray-500">{lastRefreshedAt.toLocaleTimeString()}</span>
        )}
        {tone !== 'ok' && (
          <Button variant="outline" size="sm" onClick={onJumpToDegraded}>
            Investigate
          </Button>
        )}
      </div>
    </div>
  )
}

function MiniStat({ label, value, tone }: { label: string; value: number | string; tone?: 'warn' | 'crit' }) {
  const valTone = tone === 'crit' ? 'text-red-700' : tone === 'warn' ? 'text-amber-700' : 'text-gray-900'
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-gray-500">{label}</span>
      <span className={`font-semibold tabular-nums ${valTone}`}>{value}</span>
    </span>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-600 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  )
}

function TabSubheader({ tab }: { tab: Tab }) {
  const copy: Record<Tab, { title: string; body: string }> = {
    overview: {
      title: 'Routing chains',
      body: 'How each ProductModel falls over: primary first, then fallbacks. Run a probe to send a real test call down a chain and verify failover end-to-end.',
    },
    candidates: {
      title: 'Every (provider, model) tracked',
      body: 'Live health for each candidate. Use the action menu on a row to clear/force a cooldown, retire a key, or edit when this candidate gets bypassed.',
    },
    activity: {
      title: 'Recovery worker and sticky pins',
      body: 'The recovery worker auto-probes cooled-down candidates on a schedule. Sticky pins keep a chat on the same model for the conversation lifetime.',
    },
  }
  const c = copy[tab]
  return (
    <div className="-mt-1 flex items-start gap-2 text-xs text-gray-600">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span>
        <span className="font-medium text-gray-700">{c.title}.</span> {c.body}
      </span>
    </div>
  )
}

function HelpCard({ onClose }: { onClose: () => void }) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-950">
      <div className="flex items-start justify-between gap-3">
        <div className="font-semibold">How this page is organised</div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-blue-800 hover:underline"
        >
          Hide
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-md border border-blue-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">1. Glance</div>
          <p className="mt-1 text-xs text-gray-700">
            Top banner is the headline. Green = routing is healthy. Amber/red = something is bypassed.
          </p>
        </div>
        <div className="rounded-md border border-blue-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">2. Investigate</div>
          <p className="mt-1 text-xs text-gray-700">
            Click "Investigate" or open Candidates → filter Degraded. Expand a row to see why it tripped.
          </p>
        </div>
        <div className="rounded-md border border-blue-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">3. Tune</div>
          <p className="mt-1 text-xs text-gray-700">
            Use the row menu → Edit thresholds. Pick a preset (Strict/Default/Lenient) instead of guessing numbers.
          </p>
        </div>
      </div>
      <div className="mt-3 rounded-md border border-blue-200 bg-white p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Test routing in 30 seconds</div>
        <ol className="mt-1.5 list-decimal space-y-0.5 pl-5 text-xs text-gray-700">
          <li>Open the <span className="font-medium">Chains</span> tab and click <span className="font-medium">Probe</span> on a chain — sends a real test call to every candidate and ranks them.</li>
          <li>Or, open <span className="font-medium">Candidates</span>, pick the primary, choose <span className="font-medium">Force cooldown</span> for a few minutes.</li>
          <li>Send a chat using that ProductModel — it should failover to the next link in the chain.</li>
          <li>Open <span className="font-medium">Activity</span> and click <span className="font-medium">Run recovery now</span> — the cooled candidate should come back to healthy.</li>
        </ol>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="rounded-md border border-blue-200 bg-white p-3 text-xs text-gray-700">
          <span className="font-semibold text-gray-900">Probe (manual):</span> a single test call you trigger from a chain. Fast, costs a few tokens.
        </div>
        <div className="rounded-md border border-blue-200 bg-white p-3 text-xs text-gray-700">
          <span className="font-semibold text-gray-900">Recovery scan (automatic):</span> a worker that re-probes only cooled-down candidates on a cron, with exponential backoff if probes keep failing.
        </div>
      </div>
    </div>
  )
}

function OverviewPanel({
  loading,
  chains,
  totalChains,
  chainQuery,
  onChainQuery,
  degradedChainsCount,
  isProbing,
  onRunProbe,
  onEditCandidate,
}: {
  loading: boolean
  chains: RoutingChain[]
  totalChains: number
  chainQuery: string
  onChainQuery: (s: string) => void
  degradedChainsCount: number
  isProbing: (productModelId: string) => boolean
  onRunProbe: (productModelId: string) => void
  onEditCandidate: (c: ChainCandidate) => void
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{totalChains}</span> routing chain{totalChains === 1 ? '' : 's'}
          {degradedChainsCount > 0 && (
            <span className="ml-2 text-amber-700">· {degradedChainsCount} with degraded link</span>
          )}
          <span className="ml-2 text-gray-400">·</span>
          <span className="ml-2 text-xs text-gray-500">
            Click <span className="font-medium text-gray-700">Probe</span> to send a real test call down a chain
          </span>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={chainQuery}
            onChange={e => onChainQuery(e.target.value)}
            placeholder="Search chains, providers, models…"
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white py-10 text-center text-gray-600">
          Loading…
        </div>
      ) : chains.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white py-10 text-center text-sm text-gray-500">
          {totalChains === 0 ? 'No active ProductModels.' : 'No chains match your search.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {chains.map(chain => (
            <ChainCard
              key={chain.productModelId}
              chain={chain}
              isProbing={isProbing(chain.productModelId)}
              onRunProbe={() => onRunProbe(chain.productModelId)}
              onEditCandidate={onEditCandidate}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function CandidatesPanel({
  loading,
  rows,
  totalCount,
  query,
  onQuery,
  filter,
  onFilter,
  counts,
  expandedRowKey,
  onToggleExpand,
  openMenuKey,
  onOpenMenu,
  onEditThresholds,
  onClearCooldown,
  onForceCooldown,
  onUnretireKey,
  onForceRetire,
  onToggleEnabled,
}: {
  loading: boolean
  rows: HealthRow[]
  totalCount: number
  query: string
  onQuery: (s: string) => void
  filter: CandidateFilter
  onFilter: (f: CandidateFilter) => void
  counts: { degraded: number; cooled: number; retired: number; healthy: number }
  expandedRowKey: string | null
  onToggleExpand: (k: string | null) => void
  openMenuKey: string | null
  onOpenMenu: (k: string | null) => void
  onEditThresholds: (row: HealthRow) => void
  onClearCooldown: (row: HealthRow) => void
  onForceCooldown: (row: HealthRow) => void
  onUnretireKey: (row: HealthRow) => void
  onForceRetire: (row: HealthRow) => void
  onToggleEnabled: (row: HealthRow) => void
}) {
  const filterChips: Array<{ value: CandidateFilter; label: string; count: number }> = [
    { value: 'all', label: 'All', count: totalCount },
    { value: 'degraded', label: 'Degraded', count: counts.degraded },
    { value: 'cooled', label: 'Cooled', count: counts.cooled },
    { value: 'retired', label: 'Retired', count: counts.retired },
    { value: 'healthy', label: 'Healthy', count: counts.healthy },
  ]

  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          {filterChips.map(chip => (
            <button
              key={chip.value}
              type="button"
              onClick={() => onFilter(chip.value)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                filter === chip.value
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {chip.label}
              <span className="rounded-full bg-white/70 px-1.5 text-[10px] tabular-nums text-gray-700">
                {chip.count}
              </span>
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={e => onQuery(e.target.value)}
            placeholder="Search provider, model, ProductModel…"
            className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-gray-600">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {totalCount === 0
                ? 'No candidates in catalog. Add active ProductModels to see them here.'
                : 'No candidates match this filter.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Calls (1h)</TableHead>
                  <TableHead className="text-right">Success</TableHead>
                  <TableHead className="text-right">TTFT p50/p90</TableHead>
                  <TableHead className="text-right">TPS p50</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => {
                  const key = `${row.provider}:${row.modelName}`
                  const expanded = expandedRowKey === key
                  return (
                    <CandidateRow
                      key={key}
                      rowKey={key}
                      row={row}
                      expanded={expanded}
                      menuOpen={openMenuKey === key}
                      onToggleExpand={() => onToggleExpand(expanded ? null : key)}
                      onOpenMenu={() => onOpenMenu(openMenuKey === key ? null : key)}
                      onEditThresholds={() => onEditThresholds(row)}
                      onClearCooldown={() => onClearCooldown(row)}
                      onForceCooldown={() => onForceCooldown(row)}
                      onUnretireKey={() => onUnretireKey(row)}
                      onForceRetire={() => onForceRetire(row)}
                      onToggleEnabled={() => onToggleEnabled(row)}
                    />
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  )
}

function CandidateRow({
  rowKey,
  row,
  expanded,
  menuOpen,
  onToggleExpand,
  onOpenMenu,
  onEditThresholds,
  onClearCooldown,
  onForceCooldown,
  onUnretireKey,
  onForceRetire,
  onToggleEnabled,
}: {
  rowKey: string
  row: HealthRow
  expanded: boolean
  menuOpen: boolean
  onToggleExpand: () => void
  onOpenMenu: () => void
  onEditThresholds: () => void
  onClearCooldown: () => void
  onForceCooldown: () => void
  onUnretireKey: () => void
  onForceRetire: () => void
  onToggleEnabled: () => void
}) {
  return (
    <>
      <TableRow
        className={`cursor-pointer ${row.isDegraded ? 'bg-amber-50/40 hover:bg-amber-50/70' : 'hover:bg-gray-50'}`}
        onClick={onToggleExpand}
      >
        <TableCell className="w-8 align-middle">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </TableCell>
        <TableCell>
          <div className="font-mono text-xs text-gray-900">
            {row.provider}<span className="text-gray-400">/</span>{row.modelName}
          </div>
          {row.referencedBy.length > 0 && (
            <div className="mt-0.5 text-[11px] text-gray-500" title={row.referencedBy.join(', ')}>
              used by {row.referencedBy.length === 1 ? row.referencedBy[0] : `${row.referencedBy[0]} +${row.referencedBy.length - 1}`}
            </div>
          )}
        </TableCell>
        <TableCell>
          <RoleBadge role={row.role} />
        </TableCell>
        <TableCell>
          <StateCell row={row} />
        </TableCell>
        <TableCell className="text-right tabular-nums text-sm">
          {row.totalCount}
          {row.totalCount > 0 && (
            <div className="text-[11px] text-gray-500">
              {row.successCount}✓ · {row.failureCount}✗
            </div>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums text-sm">
          {row.successRatePct === null ? '—' : `${row.successRatePct.toFixed(0)}%`}
        </TableCell>
        <TableCell className="text-right tabular-nums text-xs text-gray-700">
          {row.ttftP50Ms === null ? '—' : `${row.ttftP50Ms} / ${row.ttftP90Ms ?? '—'}`}
        </TableCell>
        <TableCell className="text-right tabular-nums text-xs text-gray-700">
          {row.tpsP50 === null ? '—' : row.tpsP50.toFixed(1)}
        </TableCell>
        <TableCell className="w-10 text-right" onClick={e => e.stopPropagation()}>
          <div className="relative inline-block">
            <button
              type="button"
              onClick={onOpenMenu}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              aria-label="Actions"
              title="Actions: cooldown, retire, edit thresholds"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                <MenuSection label="Adjust state" />
                {row.manuallyDisabled ? (
                  <MenuItem
                    icon={<Power className="h-3.5 w-3.5" />}
                    onClick={onToggleEnabled}
                    tone="ok"
                    description="Bring back into routing pool"
                  >
                    Enable model
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Power className="h-3.5 w-3.5" />}
                    onClick={onToggleEnabled}
                    tone="warn"
                    description="Take this model out of rotation entirely"
                  >
                    Disable model
                  </MenuItem>
                )}
                {row.cooldownActive ? (
                  <MenuItem
                    icon={<Snowflake className="h-3.5 w-3.5" />}
                    onClick={onClearCooldown}
                    tone="ok"
                    description="Bring back into rotation now"
                  >
                    Clear cooldown
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Snowflake className="h-3.5 w-3.5" />}
                    onClick={onForceCooldown}
                    tone="warn"
                    description="Bypass for N minutes (test failover)"
                  >
                    Force cooldown…
                  </MenuItem>
                )}
                {row.keyRetired ? (
                  <MenuItem
                    icon={<Shield className="h-3.5 w-3.5" />}
                    onClick={onUnretireKey}
                    tone="ok"
                    description="Bring retired key back online"
                  >
                    Unretire key
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Shield className="h-3.5 w-3.5" />}
                    onClick={onForceRetire}
                    tone="crit"
                    description="Permanently skip until unretired"
                  >
                    Force retire…
                  </MenuItem>
                )}
                {row.llmModelId && (
                  <>
                    <MenuDivider />
                    <MenuSection label="Configure" />
                    <MenuItem
                      icon={<Settings2 className="h-3.5 w-3.5" />}
                      onClick={onEditThresholds}
                      description="When should routing bypass this candidate?"
                    >
                      Edit thresholds…
                    </MenuItem>
                  </>
                )}
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className={row.isDegraded ? 'bg-amber-50/30' : 'bg-gray-50/60'}>
          <TableCell colSpan={9} className="py-3">
            <ExpandedRowDetails row={row} onEditThresholds={onEditThresholds} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function MenuItem({
  icon,
  children,
  onClick,
  tone,
  description,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onClick: () => void
  tone?: 'warn' | 'crit' | 'ok'
  description?: string
}) {
  const toneClass =
    tone === 'crit'
      ? 'text-red-700 hover:bg-red-50'
      : tone === 'warn'
        ? 'text-amber-700 hover:bg-amber-50'
        : tone === 'ok'
          ? 'text-emerald-700 hover:bg-emerald-50'
          : 'text-gray-700 hover:bg-gray-50'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2 px-3 py-2 text-left text-xs ${toneClass}`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block font-medium">{children}</span>
        {description && (
          <span className="mt-0.5 block text-[11px] text-gray-500">{description}</span>
        )}
      </span>
    </button>
  )
}

function MenuSection({ label }: { label: string }) {
  return (
    <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
  )
}

function MenuDivider() {
  return <div className="my-1 border-t border-gray-100" />
}

function ExpandedRowDetails({
  row,
  onEditThresholds,
}: {
  row: HealthRow
  onEditThresholds: () => void
}) {
  return (
    <div className="grid grid-cols-1 gap-4 px-2 md:grid-cols-3">
      <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Thresholds
        </div>
        <ul className="space-y-0.5 text-xs text-gray-700">
          <li>err ≥ {row.thresholds.errorRateThresholdPct}%</li>
          <li>
            ttft ≥ {row.thresholds.ttftDegradedThresholdMs}ms
            {row.thresholds.ttftPerTokenMs > 0 && ` + ${row.thresholds.ttftPerTokenMs}ms/tok`}
          </li>
          <li>tps &lt; {row.thresholds.tpsDegradedThresholdMin}</li>
          <li>min samples: {row.thresholds.minSamplesForTrigger}</li>
          <li>cooldown: {Math.round(row.thresholds.latencyCooldownMs / 1000)}s</li>
        </ul>
        {row.llmModelId && (
          <button
            type="button"
            onClick={onEditThresholds}
            className="mt-1.5 text-xs font-medium text-blue-700 hover:underline"
          >
            Edit thresholds →
          </button>
        )}
      </div>
      <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Used by ProductModels
        </div>
        {row.referencedBy.length === 0 ? (
          <div className="text-xs italic text-gray-500">—</div>
        ) : (
          <ul className="space-y-0.5 text-xs text-gray-700">
            {row.referencedBy.map(name => (
              <li key={name} className="font-mono">{name}</li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          Why degraded
        </div>
        {row.isDegraded ? (
          <div className="flex flex-wrap gap-1">
            {row.degradedReasons.map(reason => (
              <span
                key={reason}
                className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
              >
                {reason}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-xs text-emerald-700">No issues detected.</div>
        )}
      </div>
    </div>
  )
}

function StateCell({ row }: { row: HealthRow }) {
  if (!row.isDegraded) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        Healthy
      </span>
    )
  }
  const primary = row.manuallyDisabled
    ? { label: 'Disabled', cls: 'bg-gray-200 text-gray-800' }
    : row.keyRetired
      ? { label: 'Retired', cls: 'bg-red-100 text-red-800' }
      : row.cooldownActive
        ? { label: 'Cooled', cls: 'bg-blue-100 text-blue-800' }
        : { label: 'Degraded', cls: 'bg-amber-100 text-amber-800' }
  const reasonLine = row.cooldownActive && row.cooldownReason
    ? `cooldown: ${row.cooldownReason}`
    : row.degradedReasons[0]
  const extras = row.degradedReasons.length
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${primary.cls}`}>
        <AlertTriangle className="h-3 w-3" />
        {primary.label}
      </span>
      {reasonLine && (
        <span
          className="max-w-[20rem] truncate text-[11px] text-gray-600"
          title={row.degradedReasons.join(', ')}
        >
          {reasonLine}
          {extras > 1 && ` +${extras - 1}`}
        </span>
      )}
      {row.cooldownActive && row.cooldownExpiresAtMs && (
        <span className="text-[10px] text-gray-400">
          ends {formatRelativeFuture(row.cooldownExpiresAtMs)}
        </span>
      )}
    </div>
  )
}

function formatRelativeFuture(epochMs: number): string {
  const deltaMs = epochMs - Date.now()
  if (deltaMs <= 0) return 'now'
  const deltaSec = Math.round(deltaMs / 1000)
  if (deltaSec < 60) return `in ${deltaSec}s`
  const deltaMin = Math.round(deltaSec / 60)
  if (deltaMin < 60) return `in ${deltaMin}m`
  const deltaHr = Math.round(deltaMin / 60)
  return `in ${deltaHr}h`
}

function ActivityPanel({
  recoveryStatus,
  stickySummary,
  onRunRecoveryNow,
  isRecoveryRunning,
}: {
  recoveryStatus: RecoveryStatus | null
  stickySummary: StickySummary | null
  onRunRecoveryNow: () => void
  isRecoveryRunning: boolean
}) {
  return (
    <section className="space-y-4">
      {recoveryStatus ? (
        <RecoveryStatusCard
          status={recoveryStatus}
          onRunNow={onRunRecoveryNow}
          isRunning={isRecoveryRunning}
        />
      ) : (
        <Card>
          <CardContent className="py-6 text-sm text-gray-500">Loading recovery worker…</CardContent>
        </Card>
      )}

      {stickySummary && stickySummary.sample.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-left text-base">
              Sticky pin sample
              <span className="ml-2 text-xs font-normal text-gray-500">
                First {stickySummary.sample.length} of {stickySummary.totalPinned} active
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chat ID</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Pinned ProductModel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stickySummary.sample.map(pin => (
                  <TableRow key={pin.chatId}>
                    <TableCell className="font-mono text-xs">{pin.chatId}</TableCell>
                    <TableCell className="text-xs text-gray-700">{pin.provider}</TableCell>
                    <TableCell className="font-mono text-xs">{pin.modelName}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">
                      {pin.productModelId || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </section>
  )
}

function RecoveryStatusCard({
  status,
  onRunNow,
  isRunning,
}: {
  status: RecoveryStatus
  onRunNow: () => void
  isRunning: boolean
}) {
  const lastRunLabel = status.lastRunMs ? formatRelative(status.lastRunMs) : 'never'
  const nextRunLabel =
    status.nextRunMs && status.nextRunMs > Date.now()
      ? formatCountdown(status.nextRunMs - Date.now())
      : 'imminent'
  const intervalMin = Math.round(status.intervalMs / 60_000)
  const events = status.recentEvents.slice(0, 8)

  const stateStyle =
    status.workerState === 'active'
      ? 'bg-emerald-100 text-emerald-800'
      : status.workerState === 'stalled'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-gray-100 text-gray-700'
  const stateLabel =
    status.workerState === 'active'
      ? 'Active'
      : status.workerState === 'stalled'
        ? 'Stalled'
        : 'Never run'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-left text-base">
              Recovery worker
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${stateStyle}`}>
                {stateLabel}
              </span>
            </CardTitle>
            <p className="mt-1 text-xs text-gray-500">
              Auto-probes only the candidates currently in cooldown. Backs off exponentially while probes keep failing; auto-retires after enough consecutive failures.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRunNow}
            disabled={isRunning}
            title="Run a recovery scan immediately, bypassing the cron schedule."
          >
            {isRunning ? 'Running…' : 'Run recovery now'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <RecoveryMeta label="Interval" value={`every ${intervalMin} min`} />
          <RecoveryMeta label="Last run" value={lastRunLabel} />
          <RecoveryMeta label="Next run" value={nextRunLabel} />
          <RecoveryMeta
            label="Extended cooldowns"
            value={String(status.extendedCooldowns.length)}
            tone={status.extendedCooldowns.length > 0 ? 'warn' : undefined}
          />
        </dl>

        {status.extendedCooldowns.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
            <div className="mb-1 flex items-center gap-1.5 font-medium text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5" />
              Candidates in extended backoff
            </div>
            <ul className="space-y-1 text-amber-800">
              {status.extendedCooldowns.map(c => (
                <li key={`${c.provider}:${c.modelName}`} className="font-mono">
                  {c.provider} / {c.modelName} — failed recovery {c.consecutiveFailures}×
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Recent activity
          </div>
          {events.length === 0 ? (
            <div className="text-xs italic text-gray-500">No recovery events yet.</div>
          ) : (
            <ul className="space-y-1 text-xs">
              {events.map((event, idx) => (
                <li
                  key={`${event.timestampMs}-${idx}`}
                  className="flex items-center justify-between gap-3 rounded border border-gray-100 bg-gray-50 px-3 py-1.5"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <RecoveryOutcomePill outcome={event.outcome} />
                    <span className="font-mono text-gray-900">
                      {event.provider} / {event.modelName}
                    </span>
                    {event.consecutiveFailures !== undefined && (
                      <span className="text-gray-600">
                        ×{event.consecutiveFailures} ·{' '}
                        {Math.round((event.backoffMs ?? 0) / 60_000)}m cooldown
                      </span>
                    )}
                    {event.error && (
                      <span className="min-w-0 truncate text-gray-500" title={event.error}>
                        {event.error}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-gray-500">{formatRelative(event.timestampMs)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function RecoveryMeta({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  const valueClass = tone === 'warn' ? 'text-amber-700' : 'text-gray-900'
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`text-sm font-medium ${valueClass}`}>{value}</dd>
    </div>
  )
}

function probeFailureLabel(
  classification: string | null,
  errorMessage: string | null
): string {
  if (classification) return classification
  if (errorMessage?.startsWith('skipped:')) return 'skipped'
  return 'failed'
}

function RecoveryOutcomePill({ outcome }: { outcome: RecoveryEvent['outcome'] }) {
  const map: Record<RecoveryEvent['outcome'], { label: string; className: string }> = {
    recovered: { label: 'recovered', className: 'bg-emerald-100 text-emerald-800' },
    extendedCooldown: { label: 'extended', className: 'bg-amber-100 text-amber-800' },
    probeError: { label: 'error', className: 'bg-red-100 text-red-800' },
    autoRetired: { label: 'auto-retired', className: 'bg-red-200 text-red-900' },
  }
  const { label, className } = map[outcome]
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  )
}

function ChainCard({
  chain,
  isProbing,
  onRunProbe,
  onEditCandidate,
}: {
  chain: RoutingChain
  isProbing: boolean
  onRunProbe: () => void
  onEditCandidate: (candidate: ChainCandidate) => void
}) {
  const totalCalls = chain.candidates.reduce((sum, c) => sum + c.totalCount, 0)
  const anyDegraded = chain.candidates.some(c => c.isDegraded)
  const hasFallback = chain.candidates.some(c => c.role !== 'primary')

  return (
    <div
      className={`rounded-lg border bg-white ${
        anyDegraded ? 'border-amber-200' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex h-2 w-2 shrink-0 rounded-full ${
                anyDegraded ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              aria-hidden
            />
            <div className="truncate text-sm font-semibold text-gray-900">{chain.displayName}</div>
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {chain.candidates.length} candidate{chain.candidates.length === 1 ? '' : 's'} · {totalCalls} call
            {totalCalls === 1 ? '' : 's'} (1h)
          </div>
        </div>
        <Button
          size="sm"
          onClick={onRunProbe}
          disabled={isProbing}
          className="bg-blue-600 hover:bg-blue-700"
          title="Send a real test call to every candidate in this chain to measure live TTFT/TPS and apply cooldowns to failing ones. Costs a small amount of tokens per probe."
        >
          <Zap className={`mr-1 h-3.5 w-3.5 ${isProbing ? 'animate-pulse' : ''}`} />
          {isProbing ? 'Probing…' : 'Probe chain'}
        </Button>
      </div>
      <ol className="divide-y divide-gray-100">
        {chain.candidates.map((candidate, idx) => (
          <ChainNode
            key={`${candidate.provider}:${candidate.modelName}`}
            candidate={candidate}
            stepNumber={idx + 1}
            isLast={idx === chain.candidates.length - 1}
            onEdit={() => onEditCandidate(candidate)}
          />
        ))}
        {!hasFallback && (
          <li className="px-4 py-3 text-xs italic text-gray-500">
            No admin-configured fallbacks. On primary failure, routing falls through to the org's
            chat-model last-resort (resolved per-org at request time).
          </li>
        )}
      </ol>
    </div>
  )
}

function ChainNode({
  candidate,
  stepNumber,
  isLast,
  onEdit,
}: {
  candidate: ChainCandidate
  stepNumber: number
  isLast: boolean
  onEdit: () => void
}) {
  const stepLabel =
    candidate.role === 'primary'
      ? 'Primary'
      : candidate.role === 'orgLastResort'
        ? 'Org last-resort'
        : `Fallback #${candidate.fallbackOrder}`

  return (
    <li className="relative px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center">
          <div
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
              candidate.isDegraded ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
            }`}
          >
            {stepNumber}
          </div>
          {!isLast && <div className="mt-1 h-full w-px bg-gray-200" />}
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {stepLabel}
            </span>
            <span className="font-mono text-xs text-gray-900">
              {candidate.provider} / {candidate.modelName}
            </span>
            {candidate.llmModelId && (
              <button
                type="button"
                onClick={onEdit}
                className="ml-auto rounded px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50"
              >
                Edit thresholds
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
            <span className="tabular-nums">
              {candidate.totalCount} call{candidate.totalCount === 1 ? '' : 's'}
            </span>
            <span className="tabular-nums">
              {candidate.successRatePct === null ? '— success' : `${candidate.successRatePct.toFixed(0)}% ok`}
            </span>
            <span className="tabular-nums">
              {candidate.ttftP90Ms === null ? '— ttft' : `${candidate.ttftP90Ms}ms ttft p90`}
            </span>
            <span className="tabular-nums">
              {candidate.tpsP50 === null ? '— tps' : `${candidate.tpsP50.toFixed(1)} tps p50`}
            </span>
          </div>
          {candidate.isDegraded && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {candidate.degradedReasons.map(reason => (
                <span
                  key={reason}
                  className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                >
                  {reason}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

function RoleBadge({ role }: { role: HealthRow['role'] }) {
  const map: Record<HealthRow['role'], { label: string; className: string }> = {
    productModelPrimary: { label: 'Primary', className: 'bg-blue-100 text-blue-800' },
    fallback: { label: 'Fallback', className: 'bg-gray-100 text-gray-700' },
    both: { label: 'Primary + Fallback', className: 'bg-violet-100 text-violet-800' },
  }
  const { label, className } = map[role]
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}

function ProbeResultsModal({
  report,
  onClose,
}: {
  report: ProbeReport
  onClose: () => void
}) {
  const sortedResults = [...report.results].sort((a, b) => {
    if (a.success !== b.success) return a.success ? -1 : 1
    return (b.score ?? -1) - (a.score ?? -1)
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Probe results</h2>
            <p className="mt-1 text-xs text-gray-600">
              Each candidate received a small test call. Measurements were recorded into health stats; failing candidates were cooled or retired automatically per error class.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Run at {new Date(report.runAtMs).toLocaleTimeString()}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          {report.suggestion}
        </div>

        <div className="mt-4 overflow-hidden rounded border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Rank</th>
                <th className="px-3 py-2 text-left">Candidate</th>
                <th className="px-3 py-2 text-right">TTFT</th>
                <th className="px-3 py-2 text-right">Duration</th>
                <th className="px-3 py-2 text-right">TPS</th>
                <th className="px-3 py-2 text-left">Outcome</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedResults.map((r, idx) => {
                const isBest = `${r.provider}:${r.modelName}` === report.bestKey
                const isWorst = `${r.provider}:${r.modelName}` === report.worstKey
                return (
                  <tr
                    key={`${r.provider}:${r.modelName}`}
                    className={!r.success ? 'bg-amber-50/40' : undefined}
                  >
                    <td className="px-3 py-2">
                      {isBest && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          Best
                        </span>
                      )}
                      {isWorst && !isBest && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                          Worst
                        </span>
                      )}
                      {!isBest && !isWorst && (
                        <span className="text-xs text-gray-400">{idx + 1}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-900">
                      {r.provider}/{r.modelName}
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">
                        {r.role}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.ttftMs !== null ? `${r.ttftMs}ms` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.durationMs}ms</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.tpsTokensPerSec !== null ? r.tpsTokensPerSec.toFixed(1) : '—'}
                      {r.outputTokens !== null && (
                        <div className="text-[10px] text-gray-500">{r.outputTokens} tok</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.success ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">
                          Success
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
                          {probeFailureLabel(r.errorClassification, r.errorMessage)}
                        </span>
                      )}
                      {r.actionTaken !== 'none' && (
                        <div className="mt-0.5 text-[10px] text-gray-600">
                          {r.actionTaken === 'markedCooldown' ? 'cooldown applied' : 'key retired'}
                        </div>
                      )}
                      {!r.success && r.errorMessage && (
                        <div
                          className="mt-1 max-w-[28rem] whitespace-pre-wrap break-words text-[11px] leading-snug text-gray-700"
                          title={r.errorMessage}
                        >
                          {r.errorMessage}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

type ThresholdFormStrings = Record<keyof Thresholds, string>

const THRESHOLD_BOUNDS: Record<keyof Thresholds, { min: number; max: number }> = {
  errorRateThresholdPct: { min: 1, max: 100 },
  ttftDegradedThresholdMs: { min: 100, max: Number.MAX_SAFE_INTEGER },
  ttftPerTokenMs: { min: 0, max: Number.MAX_SAFE_INTEGER },
  tpsDegradedThresholdMin: { min: 1, max: Number.MAX_SAFE_INTEGER },
  minSamplesForTrigger: { min: 1, max: Number.MAX_SAFE_INTEGER },
  latencyCooldownMs: { min: 1000, max: Number.MAX_SAFE_INTEGER },
  recoverySkipAfterFailures: { min: 1, max: 100 },
  autoRetireAfterFailures: { min: 1, max: 100 },
}

function thresholdsToStrings(t: Thresholds): ThresholdFormStrings {
  return {
    errorRateThresholdPct: String(t.errorRateThresholdPct),
    ttftDegradedThresholdMs: String(t.ttftDegradedThresholdMs),
    ttftPerTokenMs: String(t.ttftPerTokenMs),
    tpsDegradedThresholdMin: String(t.tpsDegradedThresholdMin),
    minSamplesForTrigger: String(t.minSamplesForTrigger),
    latencyCooldownMs: String(t.latencyCooldownMs),
    recoverySkipAfterFailures: String(t.recoverySkipAfterFailures),
    autoRetireAfterFailures: String(t.autoRetireAfterFailures),
  }
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs text-gray-500">{children}</p>
}

function DeadlinePreview({ baseMs, perTokenMs }: { baseMs: string; perTokenMs: string }) {
  const base = Number(baseMs)
  const perTok = Number(perTokenMs)
  if (!Number.isFinite(base) || !Number.isFinite(perTok)) return null
  const rows: Array<{ label: string; tokens: number }> = [
    { label: '1k', tokens: 1_000 },
    { label: '10k', tokens: 10_000 },
    { label: '50k', tokens: 50_000 },
  ]
  const fmt = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`)
  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-2.5 text-xs">
      <div className="font-mono text-[11px] text-gray-600">
        deadline = {fmt(base)} + {perTok}ms × tokens
      </div>
      <div className="mt-1.5 grid grid-cols-3 gap-2">
        {rows.map(r => (
          <div key={r.label} className="flex items-center justify-between rounded bg-gray-50 px-2 py-1">
            <span className="text-gray-500">{r.label} tok</span>
            <span className="font-mono font-medium text-gray-900">{fmt(base + perTok * r.tokens)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ThresholdEditorModal({
  target,
  onCancel,
  onSave,
}: {
  target: ThresholdEditTarget
  onCancel: () => void
  onSave: (target: ThresholdEditTarget, next: Thresholds) => Promise<void>
}) {
  const [form, setForm] = useState<ThresholdFormStrings>(() =>
    thresholdsToStrings(target.thresholds),
  )
  const [saving, setSaving] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const updateField = (key: keyof Thresholds, raw: string) => {
    setForm(prev => ({ ...prev, [key]: raw }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const parsed: Thresholds = {
      errorRateThresholdPct: clampField('errorRateThresholdPct', form.errorRateThresholdPct, target.thresholds.errorRateThresholdPct),
      ttftDegradedThresholdMs: clampField('ttftDegradedThresholdMs', form.ttftDegradedThresholdMs, target.thresholds.ttftDegradedThresholdMs),
      ttftPerTokenMs: clampField('ttftPerTokenMs', form.ttftPerTokenMs, target.thresholds.ttftPerTokenMs),
      tpsDegradedThresholdMin: clampField('tpsDegradedThresholdMin', form.tpsDegradedThresholdMin, target.thresholds.tpsDegradedThresholdMin),
      minSamplesForTrigger: clampField('minSamplesForTrigger', form.minSamplesForTrigger, target.thresholds.minSamplesForTrigger),
      latencyCooldownMs: clampField('latencyCooldownMs', form.latencyCooldownMs, target.thresholds.latencyCooldownMs),
      recoverySkipAfterFailures: clampField('recoverySkipAfterFailures', form.recoverySkipAfterFailures, target.thresholds.recoverySkipAfterFailures),
      autoRetireAfterFailures: clampField('autoRetireAfterFailures', form.autoRetireAfterFailures, target.thresholds.autoRetireAfterFailures),
    }
    setForm(thresholdsToStrings(parsed))
    setSaving(true)
    try {
      await onSave(target, parsed)
    } finally {
      setSaving(false)
    }
  }

  const formatSeconds = (raw: string): string | null => {
    const n = Number(raw)
    if (!Number.isFinite(n) || n <= 0) return null
    if (n < 1000) return null
    if (n < 60_000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}s`
    const minutes = n / 60_000
    return `${minutes.toFixed(minutes % 1 === 0 ? 0 : 1)}m`
  }
  const ttftBaseLabel = formatSeconds(form.ttftDegradedThresholdMs)
  const cooldownLabel = formatSeconds(form.latencyCooldownMs)

  const applyPreset = (key: keyof typeof THRESHOLD_PRESETS) => {
    setForm(thresholdsToStrings(THRESHOLD_PRESETS[key].values))
  }

  const isAtDefault = (() => {
    const t = THRESHOLD_PRESETS.default.values
    return (
      Number(form.errorRateThresholdPct) === t.errorRateThresholdPct &&
      Number(form.ttftDegradedThresholdMs) === t.ttftDegradedThresholdMs &&
      Number(form.ttftPerTokenMs) === t.ttftPerTokenMs &&
      Number(form.tpsDegradedThresholdMin) === t.tpsDegradedThresholdMin &&
      Number(form.minSamplesForTrigger) === t.minSamplesForTrigger &&
      Number(form.latencyCooldownMs) === t.latencyCooldownMs &&
      Number(form.recoverySkipAfterFailures) === t.recoverySkipAfterFailures &&
      Number(form.autoRetireAfterFailures) === t.autoRetireAfterFailures
    )
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900">Routing thresholds</h2>
        <p className="mt-1 font-mono text-xs text-gray-600">
          {target.provider} / {target.modelName}
        </p>
        <p className="mt-2 text-xs text-gray-600">
          These settings answer one question: <span className="font-medium text-gray-900">when should routing bypass this candidate?</span> Triggers fire once at least min samples have accumulated AND any threshold is breached. On breach, the candidate is cooled down and routing falls through to the next chain link.
        </p>

        <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Presets</div>
            <button
              type="button"
              onClick={() => applyPreset('default')}
              disabled={isAtDefault}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
              title="Reset to platform defaults"
            >
              <RotateCcw className="h-3 w-3" />
              Reset to default
            </button>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {(Object.keys(THRESHOLD_PRESETS) as Array<keyof typeof THRESHOLD_PRESETS>).map(k => (
              <button
                key={k}
                type="button"
                onClick={() => applyPreset(k)}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-50"
              >
                <div className="text-xs font-semibold text-gray-900">{THRESHOLD_PRESETS[k].label}</div>
                <div className="mt-0.5 text-[10px] leading-snug text-gray-500">
                  {THRESHOLD_PRESETS[k].description}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Pick a preset, then fine-tune below if needed.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Common
            </div>
            <div className="space-y-3">
              <div>
                <Input
                  label="Error rate threshold (%)"
                  type="number"
                  min={1}
                  max={100}
                  value={form.errorRateThresholdPct}
                  onChange={e => updateField('errorRateThresholdPct', e.target.value)}
                />
                <FieldHint>
                  Bypass this candidate when the rolling failure rate climbs above this percent (and min samples is reached).
                </FieldHint>
              </div>
              <div>
                <Input
                  label={`TTFT base deadline (ms)${ttftBaseLabel ? ` · ${ttftBaseLabel}` : ''}`}
                  type="number"
                  min={100}
                  value={form.ttftDegradedThresholdMs}
                  onChange={e => updateField('ttftDegradedThresholdMs', e.target.value)}
                />
                <FieldHint>
                  Per-call cap on time waiting for the first chunk before we abort and fail over. Effective cap = base + per-token × input tokens.
                </FieldHint>
              </div>
              <div>
                <Input
                  label="TPS minimum (tokens/sec)"
                  type="number"
                  min={1}
                  value={form.tpsDegradedThresholdMin}
                  onChange={e => updateField('tpsDegradedThresholdMin', e.target.value)}
                />
                <FieldHint>
                  After a successful stream, trip a cooldown if the windowed median tokens/sec drops below this (and min samples is reached).
                </FieldHint>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="flex w-full items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 hover:text-gray-900"
          >
            {showAdvanced ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            Advanced
            <span className="ml-1 normal-case tracking-normal text-gray-400">
              · per-token TTFT, sample size, cooldown
            </span>
          </button>

          {showAdvanced && (
            <div className="space-y-3 rounded-md bg-gray-50 p-3">
              <div>
                <Input
                  label="TTFT per input-token (ms)"
                  type="number"
                  min={0}
                  value={form.ttftPerTokenMs}
                  onChange={e => updateField('ttftPerTokenMs', e.target.value)}
                />
                <FieldHint>
                  Extra ms added to the TTFT deadline for each input token. Useful for reasoning models whose prefill scales with context. Set 0 to keep the deadline flat.
                </FieldHint>
                <DeadlinePreview
                  baseMs={form.ttftDegradedThresholdMs}
                  perTokenMs={form.ttftPerTokenMs}
                />
              </div>
              <div>
                <Input
                  label="Min samples before trigger"
                  type="number"
                  min={1}
                  value={form.minSamplesForTrigger}
                  onChange={e => updateField('minSamplesForTrigger', e.target.value)}
                />
                <FieldHint>
                  How many calls must accumulate in the window before error-rate / TPS thresholds are trusted. The per-call TTFT deadline still fires regardless.
                </FieldHint>
              </div>
              <div>
                <Input
                  label={`Latency cooldown (ms)${cooldownLabel ? ` · ${cooldownLabel}` : ''}`}
                  type="number"
                  min={1000}
                  value={form.latencyCooldownMs}
                  onChange={e => updateField('latencyCooldownMs', e.target.value)}
                />
                <FieldHint>
                  How long to skip the candidate after a TTFT or TPS cooldown fires. The recovery worker may extend this exponentially while probes keep failing.
                </FieldHint>
              </div>
              <div>
                <Input
                  label="Skip recovery probe after N consecutive failures"
                  type="number"
                  min={1}
                  max={100}
                  value={form.recoverySkipAfterFailures}
                  onChange={e => updateField('recoverySkipAfterFailures', e.target.value)}
                />
                <FieldHint>
                  Once the recovery worker has logged this many consecutive probe failures, further probes short-circuit (no upstream call). Manual intervention required to reset.
                </FieldHint>
              </div>
              <div>
                <Input
                  label="Auto-retire candidate after N consecutive failures"
                  type="number"
                  min={1}
                  max={100}
                  value={form.autoRetireAfterFailures}
                  onChange={e => updateField('autoRetireAfterFailures', e.target.value)}
                />
                <FieldHint>
                  When the recovery worker reaches this many consecutive failures, the candidate is permanently retired and won't be routed to until manually un-retired.
                </FieldHint>
              </div>
            </div>
          )}

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-600">
            <span className="font-medium text-gray-800">After saving:</span> open the chain that uses this candidate and click Probe to confirm routing still picks the right link, or watch the Activity tab to see if a recovery scan reflects the new threshold.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function clampField(
  key: keyof Thresholds,
  raw: string,
  fallback: number,
): number {
  const { min, max } = THRESHOLD_BOUNDS[key]
  const trimmed = raw.trim()
  if (trimmed === '') return Math.max(min, Math.min(max, fallback))
  const parsed = parseInt(trimmed, 10)
  if (Number.isNaN(parsed)) return Math.max(min, Math.min(max, fallback))
  return Math.max(min, Math.min(max, parsed))
}

function formatRelative(timestampMs: number): string {
  const deltaMs = Date.now() - timestampMs
  if (deltaMs < 0) return 'just now'
  const seconds = Math.round(deltaMs / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function formatCountdown(deltaMs: number): string {
  const seconds = Math.max(0, Math.round(deltaMs / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  return `${minutes}m`
}
